import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { format } from 'date-fns'
import {
  UsersIcon,
  MagnifyingGlassIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  ArrowPathIcon,
  DocumentArrowDownIcon,
  FunnelIcon,
  BookOpenIcon,
  UserIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '@/hooks/useAuth'
import { useUserRole } from '@/hooks/useUserRole'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import {
  getAttendance,
  getAttendanceStats,
  updateAttendance,
  markPresent,
  markAbsent,
  markLate,
  bulkMarkAllPresent,
  bulkMarkAllAbsent,
  type AttendanceRecord,
  type AttendanceStats,
  type GetAttendanceParams
} from '@/api/attendanceApi'
import { getUsers, type User } from '@/api/usersApi'
import { getSessions, type Session } from '@/api/sessionsApi'
import { getStreams, type Stream } from '@/api/academicApi'
import { institutionsApi, type Institution } from '@/api/institutionsApi'
import toast from 'react-hot-toast'

const AttendancePage: React.FC = () => {
  const { user } = useAuth()
  const { userRole } = useUserRole()
  
  // State for attendance records
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<AttendanceStats>({
    totalAttendanceRecords: 0,
    totalAttendance: 0,
    presentCount: 0,
    absentCount: 0,
    lateCount: 0,
    totalHours: 0,
    avgHours: 0,
    attendanceRate: 0
  })
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [pageSize] = useState(20)
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [dateFromFilter, setDateFromFilter] = useState('')
  const [dateToFilter, setDateToFilter] = useState('')
  const [teacherFilter, setTeacherFilter] = useState('')
  const [sessionFilter, setSessionFilter] = useState('')
  const [studentFilter, setStudentFilter] = useState('')
  const [streamFilter, setStreamFilter] = useState('')
  const [institutionFilter, setInstitutionFilter] = useState('')
  const [sortBy, setSortBy] = useState<string>('checkInTime')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  
  // UI state
  const [showFilters, setShowFilters] = useState(false)
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table')
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [selectedRecords, setSelectedRecords] = useState<Set<string>>(new Set())
  
  // Data for filters
  const [teachers, setTeachers] = useState<User[]>([])
  const [sessions, setSessions] = useState<Session[]>([])
  const [students, setStudents] = useState<User[]>([])
  const [streams, setStreams] = useState<Stream[]>([])
  const [institutions, setInstitutions] = useState<Institution[]>([])
  
  useEffect(() => {
    fetchAttendance()
    fetchStats()
    fetchFilterData()
  }, [currentPage, searchQuery, statusFilter, dateFromFilter, dateToFilter, 
      teacherFilter, sessionFilter, studentFilter, streamFilter, institutionFilter, sortBy, sortOrder])

  const fetchAttendance = async () => {
    setLoading(true)
    try {
      const params: GetAttendanceParams = {
        page: currentPage,
        limit: pageSize,
        search: searchQuery.trim() || undefined,
        status: statusFilter === 'all' ? undefined : (statusFilter as any),
        teacherId: teacherFilter || undefined,
        sessionId: sessionFilter || undefined,
        studentId: studentFilter || undefined,
        streamId: streamFilter || undefined,
        institutionId: institutionFilter || undefined,
        dateFrom: dateFromFilter || undefined,
        dateTo: dateToFilter || undefined,
        sortBy: sortBy as any,
        sortOrder
      }

      const response = await getAttendance(params)
      // Handle both response formats: attendance or attendanceRecords
      setAttendanceRecords(response.data?.attendance || response.data?.attendanceRecords || [])
      setTotalPages(response.data?.pagination?.totalPages || 1)
      setTotalRecords(response.data?.pagination?.totalRecords || 0)
    } catch (error) {
      console.error('Failed to fetch attendance:', error)
      toast.error('Failed to load attendance records')
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const params = {
        teacherId: teacherFilter || undefined,
        streamId: streamFilter || undefined,
        studentId: studentFilter || undefined,
        institutionId: institutionFilter || undefined,
        dateFrom: dateFromFilter || undefined,
        dateTo: dateToFilter || undefined
      }

      const response = await getAttendanceStats(params)
      // Handle response data with fallbacks
      const data = response.data || response || {}
      setStats({
        totalAttendanceRecords: data.totalAttendanceRecords || data.totalAttendance || 0,
        totalAttendance: data.totalAttendance || data.totalAttendanceRecords || 0,
        presentCount: data.presentCount || 0,
        absentCount: data.absentCount || 0,
        lateCount: data.lateCount || 0,
        totalHours: data.totalHours || 0,
        avgHours: data.avgHours || 0,
        attendanceRate: data.attendanceRate || 0
      })
    } catch (error) {
      console.error('Failed to fetch stats:', error)
    }
  }

  const fetchFilterData = async () => {
    try {
      // Fetch teachers
      const usersResponse = await getUsers()
      const teacherUsers = usersResponse.users.filter(u => 
        u.role?.toLowerCase().includes('teacher')
      )
      setTeachers(teacherUsers)

      // Fetch students
      const studentUsers = usersResponse.users.filter(u => 
        u.role?.toLowerCase().includes('student')
      )
      setStudents(studentUsers)

      // Fetch streams
      const streamsResponse = await getStreams()
      setStreams(streamsResponse)

      // Fetch recent sessions
      const sessionsResponse = await getSessions({ limit: 50 })
      setSessions(sessionsResponse.data.sessions || [])

      // Fetch institutions if super admin
      if (user?.role === 'SUPER_ADMIN') {
        const institutionsResponse = await institutionsApi.getInstitutions()
        const institutionsData = (institutionsResponse as any)?.data?.institutions
          || (institutionsResponse as any)?.data
          || (institutionsResponse as any)?.institutions
          || institutionsResponse
        setInstitutions(Array.isArray(institutionsData) ? institutionsData : [])
      }
    } catch (error) {
      console.error('Failed to fetch filter data:', error)
    }
  }

  const handleQuickStatusChange = async (record: AttendanceRecord, newStatus: 'present' | 'absent' | 'late') => {
    setActionLoading(`${record.sessionId}-${record.studentId}`)
    try {
      if (newStatus === 'present') {
        await markPresent(record.sessionId, record.studentId)
      } else if (newStatus === 'absent') {
        await markAbsent(record.sessionId, record.studentId)
      } else if (newStatus === 'late') {
        await markLate(record.sessionId, record.studentId)
      }
      
      toast.success(`Student marked as ${newStatus}`)
      fetchAttendance()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update attendance')
    } finally {
      setActionLoading(null)
    }
  }

  const handleBulkAction = async (action: 'present' | 'absent') => {
    if (selectedRecords.size === 0) {
      toast.error('Please select attendance records to update')
      return
    }

    setActionLoading('bulk')
    try {
      const recordsBySession = new Map<string, string[]>()
      
      selectedRecords.forEach(recordId => {
        const record = attendanceRecords.find(r => `${r.sessionId}-${r.studentId}` === recordId)
        if (record) {
          if (!recordsBySession.has(record.sessionId)) {
            recordsBySession.set(record.sessionId, [])
          }
          recordsBySession.get(record.sessionId)!.push(record.studentId)
        }
      })

      const promises = Array.from(recordsBySession.entries()).map(([sessionId, studentIds]) => {
        return action === 'present' 
          ? bulkMarkAllPresent(sessionId, studentIds)
          : bulkMarkAllAbsent(sessionId, studentIds)
      })

      await Promise.all(promises)
      toast.success(`Bulk updated ${selectedRecords.size} records as ${action}`)
      setSelectedRecords(new Set())
      fetchAttendance()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to bulk update attendance')
    } finally {
      setActionLoading(null)
    }
  }

  const toggleRecordSelection = (sessionId: string, studentId: string) => {
    const recordId = `${sessionId}-${studentId}`
    const newSelected = new Set(selectedRecords)
    
    if (newSelected.has(recordId)) {
      newSelected.delete(recordId)
    } else {
      newSelected.add(recordId)
    }
    
    setSelectedRecords(newSelected)
  }

  const selectAllVisible = () => {
    const allVisible = new Set(
      attendanceRecords.map(record => `${record.sessionId}-${record.studentId}`)
    )
    setSelectedRecords(allVisible)
  }

  const clearSelection = () => {
    setSelectedRecords(new Set())
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      present: { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon },
      absent: { color: 'bg-red-100 text-red-800', icon: XCircleIcon },
      late: { color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon }
    }

    const config = statusConfig[status as keyof typeof statusConfig]
    if (!config) return null

    const Icon = config.icon

    return (
      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    )
  }

  const exportToCSV = () => {
    const headers = ['Student Name', 'Email', 'Session', 'Teacher', 'Date', 'Status', 'Check In', 'Check Out', 'Hours']
    const rows = attendanceRecords.map(record => [
      `${record.student?.firstName || ''} ${record.student?.lastName || ''}`.trim(),
      record.student?.email || '',
      record.sessionTitle,
      `${record.teacher?.firstName || ''} ${record.teacher?.lastName || ''}`.trim(),
      format(new Date(record.sessionDate), 'yyyy-MM-dd'),
      record.attendanceStatus,
      record.checkInTime ? format(new Date(record.checkInTime), 'HH:mm:ss') : '',
      record.checkOutTime ? format(new Date(record.checkOutTime), 'HH:mm:ss') : '',
      record.attendanceHours?.toString() || ''
    ])

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `attendance-${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <UsersIcon className="h-8 w-8 text-blue-600 mr-3" />
            Attendance Management
          </h1>
          <p className="text-gray-600">Track and manage student attendance records</p>
        </div>
        <div className="mt-4 lg:mt-0 flex flex-wrap gap-3">
          <Button
            onClick={() => setShowFilters(!showFilters)}
            variant="secondary"
            size="sm"
            className={showFilters ? 'bg-blue-100 text-blue-700' : ''}
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
          </Button>
          <Button
            onClick={() => setViewMode(viewMode === 'table' ? 'grid' : 'table')}
            variant="secondary"
            size="sm"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            {viewMode === 'table' ? 'Grid View' : 'Table View'}
          </Button>
          <Button onClick={exportToCSV} variant="secondary" size="sm">
            <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          {selectedRecords.size > 0 && (
            <>
              <Button
                onClick={() => handleBulkAction('present')}
                size="sm"
                className="bg-green-600 hover:bg-green-700"
                disabled={actionLoading === 'bulk'}
              >
                Mark Present ({selectedRecords.size})
              </Button>
              <Button
                onClick={() => handleBulkAction('absent')}
                size="sm"
                className="bg-red-600 hover:bg-red-700"
                disabled={actionLoading === 'bulk'}
              >
                Mark Absent ({selectedRecords.size})
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <ChartBarIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Records</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalAttendanceRecords || stats.totalAttendance || 0}</p>
              <p className="text-xs text-gray-400">All attendance</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Present</p>
              <p className="text-2xl font-bold text-gray-900">{stats.presentCount || 0}</p>
              <p className="text-xs text-gray-400">{stats.attendanceRate || 0}% rate</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <XCircleIcon className="h-8 w-8 text-red-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Absent</p>
              <p className="text-2xl font-bold text-gray-900">{stats.absentCount || 0}</p>
              <p className="text-xs text-gray-400">
                {(stats.totalAttendanceRecords || stats.totalAttendance || 0) > 0 ?
                  Math.round(((stats.absentCount || 0) / (stats.totalAttendanceRecords || stats.totalAttendance || 1)) * 100) : 0}%
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <ClockIcon className="h-8 w-8 text-yellow-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Avg Hours</p>
              <p className="text-2xl font-bold text-gray-900">{(stats.avgHours || 0).toFixed(1)}h</p>
              <p className="text-xs text-gray-400">{(stats.totalHours || 0).toFixed(1)}h total</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Basic Filters */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search students, sessions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-3 py-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="present">Present</option>
            <option value="absent">Absent</option>
            <option value="late">Late</option>
          </select>

          <input
            type="date"
            value={dateFromFilter}
            onChange={(e) => setDateFromFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="From date"
          />

          <input
            type="date"
            value={dateToFilter}
            onChange={(e) => setDateToFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="To date"
          />
        </div>
      </div>

      {/* Advanced Filters */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-white p-4 rounded-lg shadow border overflow-hidden"
          >
            <h3 className="text-lg font-semibold mb-4">Advanced Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Teacher</label>
                <select
                  value={teacherFilter}
                  onChange={(e) => setTeacherFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Teachers</option>
                  {teachers.map(teacher => (
                    <option key={teacher.id || teacher._id} value={teacher.id || teacher._id}>
                      {teacher.firstName} {teacher.lastName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Session</label>
                <select
                  value={sessionFilter}
                  onChange={(e) => setSessionFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Sessions</option>
                  {sessions.map(session => (
                    <option key={session._id} value={session._id}>
                      {session.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stream</label>
                <select
                  value={streamFilter}
                  onChange={(e) => setStreamFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Streams</option>
                  {streams.map(stream => (
                    <option key={stream.id || stream._id} value={stream.id || stream._id}>
                      {stream.name}
                    </option>
                  ))}
                </select>
              </div>

              {user?.role === 'SUPER_ADMIN' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Institution</label>
                  <select
                    value={institutionFilter}
                    onChange={(e) => setInstitutionFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Institutions</option>
                    {institutions.map(institution => (
                      <option key={institution.id} value={institution.id}>
                        {institution.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="checkInTime">Check In Time</option>
                  <option value="studentName">Student Name</option>
                  <option value="sessionDate">Session Date</option>
                  <option value="attendanceHours">Attendance Hours</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Attendance Records */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner />
        </div>
      ) : attendanceRecords.length === 0 ? (
        <div className="text-center py-12">
          <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No attendance records</h3>
          <p className="mt-1 text-sm text-gray-500">No records match your current filters.</p>
        </div>
      ) : viewMode === 'table' ? (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Session
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Check In
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Check Out
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hours
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Method
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {attendanceRecords.map((record, index) => {
                  const recordId = record._id || `${record.sessionId}-${record.studentId}-${index}`

                  return (
                    <tr key={recordId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <UserIcon className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {record.studentName || `${record.student?.firstName || ''} ${record.student?.lastName || ''}`}
                            </div>
                            <div className="text-xs text-gray-500">
                              {record.studentEmail || record.student?.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-mono text-gray-900">
                          {record.studentNumber || record.student?.studentId || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{record.sessionTitle || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.sessionDate ? format(new Date(record.sessionDate), 'MMM dd, yyyy') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.checkInTime ? format(new Date(record.checkInTime), 'HH:mm') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.checkOutTime ? format(new Date(record.checkOutTime), 'HH:mm') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(record.attendanceStatus || record.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.attendanceHours ? `${record.attendanceHours}h` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          record.checkInType === 'rfid' ? 'bg-blue-100 text-blue-700' :
                          record.checkInType === 'biometric' ? 'bg-purple-100 text-purple-700' :
                          record.checkInType === 'qr_code' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {record.checkInType || 'manual'}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {attendanceRecords.map((record, index) => {
            const recordId = record._id || `${record.sessionId}-${record.studentId}-${index}`

            return (
              <motion.div
                key={recordId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg shadow border p-4"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <UserIcon className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {record.studentName || `${record.student?.firstName || ''} ${record.student?.lastName || ''}`}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {record.studentNumber || record.student?.studentId || 'No ID'}
                      </p>
                    </div>
                  </div>
                  {getStatusBadge(record.attendanceStatus || record.status)}
                </div>

                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  <div className="flex items-center">
                    <BookOpenIcon className="h-4 w-4 mr-2 text-gray-400" />
                    <span>{record.sessionTitle || '-'}</span>
                  </div>
                  <div className="flex items-center">
                    <CalendarDaysIcon className="h-4 w-4 mr-2 text-gray-400" />
                    <span>{record.sessionDate ? format(new Date(record.sessionDate), 'MMM dd, yyyy') : '-'}</span>
                  </div>
                  <div className="flex items-center">
                    <ClockIcon className="h-4 w-4 mr-2 text-gray-400" />
                    <span>
                      {record.checkInTime ? format(new Date(record.checkInTime), 'HH:mm') : '-'}
                      {' → '}
                      {record.checkOutTime ? format(new Date(record.checkOutTime), 'HH:mm') : '-'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t">
                  <div className="text-sm text-gray-600">
                    {record.attendanceHours ? `${record.attendanceHours}h` : '-'}
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    record.checkInType === 'rfid' ? 'bg-blue-100 text-blue-700' :
                    record.checkInType === 'biometric' ? 'bg-purple-100 text-purple-700' :
                    record.checkInType === 'qr_code' ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {record.checkInType || 'manual'}
                  </span>
                </div>
              </motion.div>
            )
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white px-4 py-3 border rounded-lg">
          <div className="flex items-center">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span> to{' '}
              <span className="font-medium">{Math.min(currentPage * pageSize, totalRecords)}</span> of{' '}
              <span className="font-medium">{totalRecords}</span> results
            </p>
          </div>
          <div className="flex space-x-2">
            <Button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              variant="secondary"
              size="sm"
            >
              Previous
            </Button>
            <span className="flex items-center px-4 py-2 text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              variant="secondary"
              size="sm"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default AttendancePage